import { useSEO } from '@/hooks/useSEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import { aboutData } from '@/mocks/articles';

export default function About() {
  useSEO({
    title: '关于我们 - 炽热之夜电影资讯站 | 菲律宾电影华语影迷社区',
    description: '炽热之夜电影资讯站专注菲律宾优质电影内容，为华语影迷提供最深度、最专业的菲律宾电影资讯与评论。了解我们的品牌故事与团队理念。',
    keywords: '炽热之夜电影资讯站,菲律宾电影社区,关于炽热之夜,电影资讯平台',
    canonicalUrl: 'https://chirezhizhiye.ph/about',
    ogType: 'website',
  });

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于炽热之夜电影资讯站',
    description: '专注菲律宾优质电影内容的华语影迷社区',
    url: 'https://chirezhizhiye.ph/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '炽热之夜电影资讯站',
      url: 'https://chirezhizhiye.ph',
    },
  };

  return (
    <>
      <JsonLd data={aboutJsonLd} />

      <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-6">
            关于我们
          </h1>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500">
                  <i className="ri-fire-line text-2xl text-background-50"></i>
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-900">
                    {aboutData.brandName}
                  </h2>
                  <p className="text-sm text-foreground-500">{aboutData.tagline}</p>
                </div>
              </div>
              <div
                className="text-foreground-600 leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: aboutData.introduction }}
              />
            </div>

            <div className="w-full md:w-80 bg-background-100 rounded-lg p-6 border border-background-200/70 flex-shrink-0">
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                联系方式
              </h3>
              <ul className="flex flex-col gap-3">
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <span className="text-sm text-foreground-600">{aboutData.contact.email}</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 flex-shrink-0">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <span className="text-sm text-foreground-600">{aboutData.contact.wechat}</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 flex-shrink-0">
                    <i className="ri-weibo-line"></i>
                  </div>
                  <span className="text-sm text-foreground-600">{aboutData.contact.weibo}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-2">
            用户口碑
          </h2>
          <p className="text-foreground-500 text-center mb-10">听听影迷们怎么说</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {aboutData.reputation.map((item, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-lg p-6 border border-background-200/70 flex flex-col"
              >
                <div className="text-accent-500 mb-4 flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed flex-1 mb-4">
                  "{item.quote}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-background-200">
                  <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center text-accent-700 font-heading font-semibold text-sm">
                    {item.author.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{item.author}</p>
                    <p className="text-xs text-foreground-400">{item.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-900">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
            与我们联系
          </h2>
          <p className="text-foreground-300 mb-8 leading-relaxed">
            如果您有任何关于《炽热之夜》或菲律宾电影的问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们期待听到您的声音。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={`mailto:${aboutData.contact.email}`}
              className="px-6 py-3 bg-accent-500 text-background-50 rounded-md font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-send-line mr-2"></i>
              发送邮件
            </a>
          </div>
        </div>
      </section>
    </>
  );
}